import { useEffect } from 'react';

/**
 * Legacy Circle — single-page conversion landing.
 *
 * Hidden / invitation-only. Reachable at:
 *   /legacy-circle  (canonical)
 *   /the-legacy-circle (alias)
 *
 * One screen. One promise. One offer. One CTA.
 */

// Registration closed on 2026-07-02. URLs preserved for a possible future re-open.
const FULL_PAY_URL = 'https://support.sisterdiamonds.com/legacycirclefullpay';
const PLAN_URL = 'https://support.sisterdiamonds.com/legacycirclepaymentplan';
// Reference to satisfy no-unused-vars during closed period without exposing links in the DOM.
void FULL_PAY_URL; void PLAN_URL;

const HERO_IMG = '/legacy/hero-group.jpg';
const BECOME_IMG = '/legacy/woman-portrait.jpeg';

const SERIF = { fontFamily: "'Cormorant Garamond', 'Playfair Display', Georgia, serif" };
const SANS = { fontFamily: "'Manrope', system-ui, sans-serif" };

const IVORY = '#FBF7F0';
const SOFT = '#F5EFE3';
const CHAMP = '#E9D9B7';
const GOLD = '#B08A45';
const TAUPE = '#8A7A66';
const BROWN = '#3A2E22';

const LegacyCirclePage = () => {
  useEffect(() => {
    document.title = 'Legacy Circle — Sister Diamonds';
    let robots = document.querySelector('meta[name="robots"]');
    if (!robots) {
      robots = document.createElement('meta');
      robots.name = 'robots';
      document.head.appendChild(robots);
    }
    const prev = robots.content;
    robots.content = 'noindex, nofollow';
    return () => { robots.content = prev || ''; };
  }, []);

  return (
    <div
      className="min-h-screen w-full relative"
      style={{ background: IVORY, color: IVORY }}
      data-testid="legacy-circle-page"
    >
      {/* HERO ============================================ */}
      <section
        className="relative w-full"
        style={{ background: IVORY }}
        data-testid="lc-hero"
      >
        {/* MOBILE-ONLY header that sits ABOVE the image */}
        <div
          className="lg:hidden px-6 sm:px-12 pt-12 pb-6"
          style={{ background: IVORY, color: BROWN }}
        >
          <p
            className="text-[10px] uppercase mb-5"
            style={{ ...SANS, color: GOLD, letterSpacing: '0.32em', fontWeight: 600 }}
          >
            Legacy Circle · Founding Cohort 2026–2027 · By Invitation
          </p>
          <h1
            className="text-[28px] sm:text-4xl leading-[1.1]"
            style={{ ...SERIF, color: BROWN, fontStyle: 'italic', fontWeight: 400, letterSpacing: '-0.015em' }}
          >
            What would it mean to spend the next year surrounded by women who{' '}
            <span style={{ color: GOLD }}>truly understand your journey?</span>
          </h1>
        </div>

        <div className="grid lg:grid-cols-12 items-stretch">
          {/* LEFT — Editorial text panel on ivory (mobile: 2nd; desktop: 1st) */}
          <div
            className="lg:col-span-6 lg:order-1 order-2 px-6 sm:px-12 lg:pl-20 lg:pr-16 py-8 sm:py-12 lg:py-24 flex flex-col justify-center"
            style={{ color: BROWN }}
          >
            {/* Eyebrow — DESKTOP only (mobile shows it above the image) */}
            <p
              className="hidden lg:block text-sm uppercase mb-8"
              style={{ ...SANS, color: GOLD, letterSpacing: '0.32em', fontWeight: 600 }}
              data-testid="lc-eyebrow"
            >
              Legacy Circle · Founding Cohort 2026–2027 · By Invitation
            </p>

            {/* H1 — DESKTOP only (mobile shows it above the image) */}
            <h1
              className="hidden lg:block lg:text-5xl xl:text-6xl leading-[1.1] max-w-xl"
              style={{ ...SERIF, color: BROWN, fontStyle: 'italic', fontWeight: 400, letterSpacing: '-0.015em' }}
              data-testid="lc-headline"
            >
              What would it mean to spend the next year surrounded by women who{' '}
              <span style={{ color: GOLD }}>truly understand your journey?</span>
            </h1>

            {/* Hairline — desktop only (mobile gets its own spacing) */}
            <div className="hidden lg:block my-6 sm:my-8" style={{ width: '4rem', height: '1px', background: GOLD }} />

            {/* Promise */}
            <p
              className="max-w-xl text-base sm:text-xl leading-relaxed mb-5"
              style={{ ...SERIF, color: BROWN, fontWeight: 400 }}
              data-testid="lc-promise"
            >
              A curated 12-month{' '}
              <span style={{ fontStyle: 'italic', color: GOLD }}>Collective</span>
              {' '}for women intentionally creating their next chapter — with support,
              accountability, truth, and community.
            </p>

            {/* Witnesses — the heart, in serif italic */}
            <p
              className="mt-3 max-w-xl text-base sm:text-xl leading-snug"
              style={{ ...SERIF, color: TAUPE, fontStyle: 'italic' }}
              data-testid="lc-witnesses"
            >
              Women who see you. Women who challenge you when you shrink.
              Women who refuse to let you disappear.
            </p>

            {/* Registration Closed — replaces prior pricing/purchase cards */}
            <div
              className="mt-12 p-7 sm:p-8 max-w-2xl"
              style={{ background: SOFT, border: `1px solid ${CHAMP}` }}
              data-testid="lc-registration-closed"
            >
              <p
                className="mb-3"
                style={{ ...SANS, color: GOLD, fontSize: '11px', letterSpacing: '0.32em', fontWeight: 600, textTransform: 'uppercase' }}
              >
                Enrollment Update
              </p>
              <p
                style={{ ...SERIF, color: BROWN, fontSize: '1.4rem', lineHeight: 1.4, fontWeight: 400 }}
              >
                Registration for this offer has ended.{' '}
                <em style={{ color: TAUPE, fontStyle: 'italic' }}>
                  Please contact us for future enrollment opportunities.
                </em>
              </p>
            </div>

            {/* Footnote */}
            <p
              className="mt-10 text-xs sm:text-sm max-w-xl"
              style={{ ...SANS, color: TAUPE, letterSpacing: '0.04em', lineHeight: 1.6 }}
              data-testid="lc-footnote"
            >
              An invitation-only experience curated by{' '}
              <span style={{ color: BROWN, fontWeight: 600 }}>Crystal Khalil</span> &amp;{' '}
              <span style={{ color: BROWN, fontWeight: 600 }}>Dr. Nicole LaBeach.</span>
            </p>
          </div>

          {/* RIGHT — Image panel (appears 1st on mobile, 2nd on desktop) */}
          <div
            className="lg:col-span-6 lg:order-2 order-1 relative overflow-hidden flex flex-col items-stretch justify-center"
            style={{ background: IVORY, minHeight: '320px' }}
          >
            <img
              src={HERO_IMG}
              alt="Sister Diamonds — a Collective of women on the beach"
              className="w-full h-auto lg:absolute lg:inset-0 lg:h-full"
              style={{ objectFit: 'contain', objectPosition: 'center' }}
            />
            {/* Sub-headline — BELOW image on mobile, OVERLAY on desktop */}
            <div
              className="px-6 sm:px-12 lg:px-14 pt-5 pb-8 sm:pb-10
                         lg:absolute lg:bottom-0 lg:left-0 lg:right-0 lg:pt-0 lg:pointer-events-none"
            >
              <div
                className="mb-3"
                style={{ width: '3rem', height: '1px', background: GOLD, opacity: 0.85 }}
              />
              <p
                className="text-lg sm:text-xl lg:text-2xl leading-[1.2] max-w-xl"
                style={{ ...SERIF, color: BROWN, fontWeight: 500, letterSpacing: '-0.005em' }}
                data-testid="lc-sub-headline"
              >
                A Room Full of Women Who{' '}
                <span style={{ fontStyle: 'italic', color: GOLD }}>
                  Refuse To Let You Abandon Yourself.
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          "WHO YOU BECOME" — conversion-boosting section below the fold.
          One screen of transformation outcomes + one anchor testimonial.
          ============================================================ */}
      <section
        className="relative px-6 sm:px-10 lg:px-16 py-24 sm:py-32"
        style={{ background: IVORY, color: BROWN }}
        data-testid="lc-become"
      >
        <div className="mx-auto max-w-6xl">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
            {/* LEFT — Portrait (1st on mobile, 1st on desktop already) */}
            <div className="lg:col-span-5 max-w-sm mx-auto lg:max-w-none w-full">
              <div
                className="relative overflow-hidden"
                style={{ aspectRatio: '4 / 5', border: `1px solid ${CHAMP}` }}
              >
                <img
                  src={BECOME_IMG}
                  alt="A confident, joyful woman — the woman you become"
                  className="absolute inset-0 w-full h-full"
                  style={{ objectFit: 'cover', objectPosition: 'center' }}
                  loading="lazy"
                />
              </div>
              <p
                className="mt-5 text-sm italic"
                style={{ ...SERIF, color: TAUPE, letterSpacing: '0.01em' }}
              >
                — Twelve months from today.
              </p>
            </div>

            {/* RIGHT — The outcomes */}
            <div className="lg:col-span-7">
              <p
                className="text-xs sm:text-sm uppercase mb-6"
                style={{ ...SANS, color: GOLD, letterSpacing: '0.32em', fontWeight: 500 }}
              >
                One Year From Now
              </p>
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl leading-tight"
                style={{ ...SERIF, color: BROWN, fontWeight: 400, letterSpacing: '-0.01em' }}
              >
                The Woman You Become{' '}
                <em style={{ color: GOLD, fontStyle: 'italic' }}>Inside Legacy Circle</em>
              </h2>
              <div className="my-8" style={{ width: '3rem', height: '1px', background: GOLD, opacity: 0.6 }} />

              <div className="grid sm:grid-cols-2 gap-x-8 gap-y-4">
                {[
                  'You trust yourself more.',
                  'You stop abandoning your needs.',
                  'You make different decisions.',
                  'You create stronger boundaries.',
                  'You stop carrying everything alone.',
                  'You have women you can call.',
                  'You feel supported.',
                  'You create a life aligned with who you are becoming.',
                ].map((line) => (
                  <div key={line} className="flex items-start gap-3">
                    <span
                      className="mt-2 inline-block flex-shrink-0"
                      style={{ width: '0.45rem', height: '0.45rem', background: GOLD, transform: 'rotate(45deg)' }}
                    />
                    <p style={{ ...SERIF, color: BROWN, fontSize: '1.05rem', lineHeight: 1.5 }}>
                      {line}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Anchor testimonial — social proof */}
          <figure
            className="mt-20 max-w-3xl mx-auto text-center"
            style={{ borderTop: `1px solid ${CHAMP}`, borderBottom: `1px solid ${CHAMP}` }}
            data-testid="lc-testimonial"
          >
            <blockquote
              className="py-12 px-4 text-xl sm:text-2xl lg:text-3xl leading-relaxed"
              style={{ ...SERIF, color: BROWN, fontStyle: 'italic', fontWeight: 400 }}
            >
              &ldquo;Sister Diamonds gave me the space to breathe, reflect, and
              strategize. The community is unlike anything I&rsquo;ve experienced.&rdquo;
            </blockquote>
          </figure>
          <figcaption className="mt-6 text-center" style={{ ...SANS }}>
            <p style={{ color: BROWN, fontSize: '0.9rem', fontWeight: 600, letterSpacing: '0.04em' }}>
              CeWyon Chandler-Ward
            </p>
            <p style={{ color: TAUPE, fontSize: '0.75rem', letterSpacing: '0.18em', textTransform: 'uppercase', marginTop: '4px' }}>
              Serial Entrepreneur · Angel Investor
            </p>
          </figcaption>

          {/* Secondary close — pulls scrollers back to CTA */}
          <div className="mt-20 text-center">
            <p
              className="mb-10 text-2xl sm:text-3xl lg:text-4xl max-w-3xl mx-auto"
              style={{ ...SERIF, color: BROWN, fontStyle: 'italic', fontWeight: 400, lineHeight: 1.3 }}
            >
              The retreat was the interruption.
              <br />
              Legacy Circle is the integration.
            </p>
            <div
              className="mx-auto p-7 sm:p-8 max-w-2xl"
              style={{ background: SOFT, border: `1px solid ${CHAMP}` }}
              data-testid="lc-become-closed"
            >
              <p
                className="mb-3"
                style={{ ...SANS, color: GOLD, fontSize: '11px', letterSpacing: '0.32em', fontWeight: 600, textTransform: 'uppercase' }}
              >
                Enrollment Update
              </p>
              <p
                style={{ ...SERIF, color: BROWN, fontSize: '1.4rem', lineHeight: 1.4, fontWeight: 400 }}
              >
                Registration for this offer has ended.{' '}
                <em style={{ color: TAUPE, fontStyle: 'italic' }}>
                  Please contact us for future enrollment opportunities.
                </em>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
          ENROLLMENT AGREEMENT + TERMS
          ============================================================ */}
      <section
        className="px-6 sm:px-10 lg:px-16 py-16 sm:py-20"
        style={{ background: '#F5EFE3', color: BROWN }}
        data-testid="lc-terms"
      >
        <div className="mx-auto max-w-3xl text-center">
          <p
            className="text-xs sm:text-sm uppercase mb-4"
            style={{ ...SANS, color: GOLD, letterSpacing: '0.32em', fontWeight: 600 }}
          >
            The Fine Print, Made Beautifully Simple
          </p>
          <h3
            className="text-2xl sm:text-3xl lg:text-4xl leading-tight"
            style={{ ...SERIF, color: BROWN, fontWeight: 400, letterSpacing: '-0.01em' }}
          >
            Before You Enroll
          </h3>
          <div className="mx-auto my-6" style={{ width: '3rem', height: '1px', background: GOLD, opacity: 0.6 }} />
          <p
            className="max-w-2xl mx-auto text-base leading-relaxed mb-10"
            style={{ ...SANS, color: BROWN }}
          >
            Your payment enrolls you in the{' '}
            <strong style={{ color: BROWN }}>Legacy Circle Founding Cohort 2026–2027</strong> —
            a 12-month commitment beginning July 2026. Please review and download both
            documents below. They will also be sent to you by email after enrollment.
          </p>

          <div className="grid sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
            <a
              href="/legacy/Legacy-Circle-Enrollment-Agreement.pdf"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="lc-terms-enrollment"
              className="group block text-left p-5 transition-all duration-300 hover:bg-white"
              style={{ background: IVORY, border: `1px solid ${CHAMP}` }}
            >
              <p
                className="mb-2"
                style={{ ...SANS, color: GOLD, fontSize: '10px', letterSpacing: '0.28em', fontWeight: 600, textTransform: 'uppercase' }}
              >
                Document · PDF
              </p>
              <p style={{ ...SERIF, color: BROWN, fontSize: '1.05rem', fontWeight: 500, lineHeight: 1.3 }}>
                Legacy Circle Founding Member Enrollment Agreement
              </p>
              <span
                className="mt-3 inline-flex items-center"
                style={{ ...SANS, color: BROWN, fontSize: '11px', letterSpacing: '0.16em', fontWeight: 600, textTransform: 'uppercase' }}
              >
                Open / Download →
              </span>
            </a>

            <a
              href="/legacy/Volition-Master-Terms-and-Privacy.docx"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="lc-terms-master"
              className="group block text-left p-5 transition-all duration-300 hover:bg-white"
              style={{ background: IVORY, border: `1px solid ${CHAMP}` }}
            >
              <p
                className="mb-2"
                style={{ ...SANS, color: GOLD, fontSize: '10px', letterSpacing: '0.28em', fontWeight: 600, textTransform: 'uppercase' }}
              >
                Document · Word
              </p>
              <p style={{ ...SERIF, color: BROWN, fontSize: '1.05rem', fontWeight: 500, lineHeight: 1.3 }}>
                Volition Master Terms of Service &amp; Privacy Policy
              </p>
              <span
                className="mt-3 inline-flex items-center"
                style={{ ...SANS, color: BROWN, fontSize: '11px', letterSpacing: '0.16em', fontWeight: 600, textTransform: 'uppercase' }}
              >
                Open / Download →
              </span>
            </a>
          </div>

          <p
            className="mt-10 text-xs leading-relaxed max-w-2xl mx-auto"
            style={{ ...SANS, color: TAUPE, letterSpacing: '0.02em' }}
          >
            By completing your enrollment payment, you acknowledge that you have read and agreed
            to the <em>Legacy Circle Founding Member Enrollment Agreement</em> and the{' '}
            <em>Volition Master Terms of Service &amp; Privacy Policy</em>. Enrollment is into
            the Founding Cohort 2026–2027 — not an open-ended membership.
          </p>
        </div>
      </section>
    </div>
  );
};

export default LegacyCirclePage;
